import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import useColorScheme from '../hooks/useColorScheme';
import TargetSelect from './TargetSelect';
import Button from './Button';
import './UnifiedHeader.css';

const UnifiedHeader = ({ user, onSignOut, onNavigateHome, onNavigateToAbout, onNavigateToPartners, onNavigateToPricing, onNavigateToDemo, onNavigateToBlog, onNavigateToShowcase, onNavigateToAuth, onNavigateToSettings, showMarketingPages = true, licenseExpired = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId, targetName } = useParams();
  const { isAdmin } = useAuth();
  const { projects, targets, fetchTargets } = useProjects();
  const { isRedIncrease, toggleColorScheme } = useColorScheme();

  const isOnAboutPage = location.pathname === '/about';
  const isOnPartnersPage = location.pathname === '/partners';
  const isOnPricingPage = location.pathname === '/pricing';
  const isOnDemoPage = location.pathname === '/demo';
  const isOnBlogPage = location.pathname === '/blog' || location.pathname.startsWith('/blog/');
  const isOnShowcasePage = location.pathname === '/showcase';

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // User dropdown menu state
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // SessionStorage helpers for tracking last visited project (copied from SideMenu)
  const getStoredProject = () => {
    try {
      return sessionStorage.getItem('lastProjectId');
    } catch (e) {
      return null;
    }
  };

  const setStoredProject = (projId) => {
    if (!projId) return;
    try {
      sessionStorage.setItem('lastProjectId', projId);
    } catch (e) {
      // Ignore storage errors
    }
  };

  const getStoredTarget = (projId) => {
    if (!projId) return null;
    try {
      return sessionStorage.getItem(`lastTarget_${projId}`);
    } catch (e) {
      return null;
    }
  };

  const setStoredTarget = (projId, target) => {
    if (!projId || !target) return;
    try {
      sessionStorage.setItem(`lastTarget_${projId}`, target);
    } catch (e) {
      // Ignore storage errors
    }
  };

  const clearStoredContext = () => {
    try {
      const storedProjectId = getStoredProject();
      sessionStorage.removeItem('lastProjectId');
      if (storedProjectId) {
        sessionStorage.removeItem(`lastTarget_${storedProjectId}`);
      }
    } catch (e) {
      // Ignore storage errors
    }
  };

  // Get effective project ID and target name
  const getEffectiveProjectId = () => {
    if (projectId) return projectId;
    return getStoredProject();
  };

  const effectiveProjectId = getEffectiveProjectId();

  const getEffectiveTargetName = () => {
    if (targetName) return targetName;
    return getStoredTarget(effectiveProjectId);
  };

  const effectiveTargetName = getEffectiveTargetName();

  // Save current project and target to sessionStorage
  useEffect(() => {
    if (projectId) {
      setStoredProject(projectId);
      if (targetName) {
        setStoredTarget(projectId, targetName);
      }
    }
  }, [projectId, targetName]);

  // Check if we should show project-specific menu items
  const shouldShowDashboard = (
    location.pathname.includes('/project/') &&
    (location.pathname.includes('/target/') || location.pathname.includes('/commit/') || location.pathname.includes('/settings'))
  ) || (
    location.pathname === '/settings' && effectiveProjectId
  );

  // Build dashboard path
  const getDashboardPath = () => {
    if (effectiveProjectId && effectiveTargetName) {
      return `/project/${effectiveProjectId}/target/${encodeURIComponent(effectiveTargetName)}`;
    }
    if (effectiveProjectId && targets[effectiveProjectId] && targets[effectiveProjectId].length > 0) {
      const firstTarget = targets[effectiveProjectId][0];
      return `/project/${effectiveProjectId}/target/${encodeURIComponent(firstTarget.name)}`;
    }
    return null;
  };

  const dashboardPath = getDashboardPath();

  // Get current project data
  const currentProject = projects?.find(p => String(p.id) === String(effectiveProjectId));
  const decodedTargetName = effectiveTargetName ? decodeURIComponent(effectiveTargetName) : null;

  // Fetch targets for the current project if not cached (needed for dropdown)
  useEffect(() => {
    if (effectiveProjectId && !targets[effectiveProjectId]) {
      // A failure is recorded in the context (errors.targets[projectId])
      fetchTargets(effectiveProjectId).catch(() => {});
    }
  }, [effectiveProjectId, targets, fetchTargets]);

  // Get available targets for the current project (for dropdown)
  const availableTargets = useMemo(() => {
    if (!effectiveProjectId) return [];
    return targets[effectiveProjectId] || [];
  }, [effectiveProjectId, targets]);

  // Determine current page suffix from the URL
  const currentPageSuffix = useMemo(() => {
    const path = location.pathname;
    const suffixes = ['/compare', '/symbols', '/budgets', '/region-config'];
    for (const suffix of suffixes) {
      if (path.endsWith(suffix)) return suffix;
    }
    return '';
  }, [location.pathname]);

  // Handle target switch from mobile dropdown
  const handleTargetSwitch = useCallback((newTargetName) => {
    if (!newTargetName || newTargetName === decodedTargetName) return;

    const encodedTarget = encodeURIComponent(newTargetName);
    const queryString = location.search;

    if (effectiveProjectId) {
      const basePath = `/project/${effectiveProjectId}/target/${encodedTarget}`;
      setIsMobileMenuOpen(false);
      navigate(`${basePath}${currentPageSuffix}${queryString}`);
    }
  }, [decodedTargetName, effectiveProjectId, currentPageSuffix, location.search, navigate]);

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Handle user menu toggle
  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const closeUserMenu = () => {
    setIsUserMenuOpen(false);
  };

  // Handle ESC key and body scroll lock for mobile menu
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    if (isMobileMenuOpen) {
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleEscape);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMobileMenuOpen]);

  // Handle click outside and ESC key for user menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        closeUserMenu();
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape' && isUserMenuOpen) {
        closeUserMenu();
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isUserMenuOpen]);

  // Wraps a SPA navigation handler for use on a real <a href>: plain left-clicks
  // stay client-side, while modifier clicks and crawlers get the href.
  const handleNavClick = (handler) => (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    handler();
  };

  // Handle navigation and close menu
  const handleNavigateHome = () => {
    closeMobileMenu();
    onNavigateHome();
  };

  const handleNavigateToAbout = () => {
    closeMobileMenu();
    onNavigateToAbout();
  };

  const handleNavigateToPartners = () => {
    closeMobileMenu();
    onNavigateToPartners();
  };

  const handleNavigateToPricing = () => {
    closeMobileMenu();
    onNavigateToPricing();
  };

  const handleNavigateToBlog = () => {
    closeMobileMenu();
    onNavigateToBlog();
  };

  const handleNavigateToShowcase = () => {
    closeMobileMenu();
    if (onNavigateToShowcase) {
      onNavigateToShowcase();
    }
  };

  // Handle navigation for SideMenu items
  const handleNavigation = (path) => {
    const isCurrentPage = location.pathname === path;

    // Clear stored project/target context when navigating to Projects page
    if (path === '/projects') {
      clearStoredContext();
    }

    closeMobileMenu();

    // If clicking on the currently active page, dispatch reload event
    if (isCurrentPage) {
      window.dispatchEvent(new CustomEvent('pageReload', {
        detail: { path }
      }));
    } else {
      navigate(path);
    }
  };

  return (
    <>
      {licenseExpired && (
        <div className="license-warning-banner">
          License expired — uploads are disabled. Please contact your administrator to update the license key.
        </div>
      )}
      <nav className="unified-header">
        <div className="unified-header-content">
        {/* Logo - Context-aware navigation */}
        <a href="/" className="unified-header-logo" onClick={handleNavClick(onNavigateHome)}>
          <img src="/membrowse-logo.svg" alt="MemBrowse" className="unified-header-logo-icon" />
          <span className="unified-header-logo-text">
            <span className="mem-text">Mem</span>
            <span className="browse-text">Browse</span>
          </span>
        </a>

        {/* Right side navigation */}
        <div className="unified-header-nav">
          {/* Docs - Visible on desktop, hidden on mobile (in hamburger menu) */}
          <a
            className="unified-header-tutorial unified-header-desktop-nav"
            href="https://docs.membrowse.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Docs
          </a>

          {showMarketingPages && (
            <a
              href="/about"
              className={`unified-header-about unified-header-desktop-nav ${isOnAboutPage ? 'active' : ''}`}
              onClick={handleNavClick(onNavigateToAbout)}
            >
              About
            </a>
          )}

          {showMarketingPages && (
            <a
              href="/partners"
              className={`unified-header-partners unified-header-desktop-nav ${isOnPartnersPage ? 'active' : ''}`}
              onClick={handleNavClick(onNavigateToPartners)}
            >
              Partners
            </a>
          )}

          {showMarketingPages && (
            <a
              href="/pricing"
              className={`unified-header-pricing unified-header-desktop-nav ${isOnPricingPage ? 'active' : ''}`}
              onClick={handleNavClick(onNavigateToPricing)}
            >
              Pricing
            </a>
          )}

          {showMarketingPages && (
            <a
              href="/blog"
              className={`unified-header-blog unified-header-desktop-nav ${isOnBlogPage ? 'active' : ''}`}
              onClick={handleNavClick(onNavigateToBlog)}
            >
              Blog
            </a>
          )}

          {showMarketingPages && (
            <a
              href="/showcase"
              className={`unified-header-showcase unified-header-desktop-nav ${isOnShowcasePage ? 'active' : ''}`}
              onClick={handleNavClick(() => onNavigateToShowcase && onNavigateToShowcase())}
            >
              Showcase
            </a>
          )}

          {/* GitHub Marketplace link */}
          {showMarketingPages && (
            <a
              href="https://github.com/membrowse/membrowse-action"
              target="_blank"
              rel="noopener noreferrer"
              className="unified-header-github unified-header-desktop-nav"
              title="View on GitHub"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
              </svg>
            </a>
          )}

          {/* GitLab CI link */}
          {showMarketingPages && (
            <a
              href="https://gitlab.com/membrowse/gitlab-ci"
              target="_blank"
              rel="noopener noreferrer"
              className="unified-header-github unified-header-desktop-nav"
              title="Get on GitLab"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M23.955 13.587l-1.342-4.135-2.664-8.189a.455.455 0 00-.867 0L16.418 9.45H7.582L4.918 1.263a.455.455 0 00-.867 0L1.387 9.452.045 13.587a.924.924 0 00.331 1.023L12 23.054l11.624-8.443a.92.92 0 00.331-1.024"/>
              </svg>
            </a>
          )}

          {/* Demo button - visible to all users when marketing pages enabled */}
          {showMarketingPages && (
            <Button
              variant="dark"
              size="medium"
              href="/demo"
              onClick={handleNavClick(onNavigateToDemo)}
              className={`unified-header-demo ${isOnDemoPage ? 'active' : ''}`}
            >
              Get Demo
            </Button>
          )}

          {/* Conditional auth section - always visible */}
          {user ? (
            <div className="unified-header-user-menu-container" ref={userMenuRef}>
              <button
                className="unified-header-user-icon"
                onClick={toggleUserMenu}
                title={`${user.user_metadata?.full_name || user.email}`}
                aria-label="User menu"
                aria-expanded={isUserMenuOpen}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
                  <path d="M4 20c0-4 3.5-6 8-6s8 2 8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>

              {/* User dropdown menu */}
              {isUserMenuOpen && (
                <div className="user-dropdown-menu">
                  <div className="user-dropdown-header">
                    <div className="user-dropdown-name">
                      {user.user_metadata?.full_name || 'User'}
                    </div>
                    <div className="user-dropdown-email">
                      {user.email}
                    </div>
                  </div>

                  <div className="user-dropdown-divider"></div>

                  <button
                    className="user-dropdown-item"
                    onClick={() => {
                      closeUserMenu();
                      window.location.href = '/projects';
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Projects
                  </button>

                  {isAdmin && (
                    <button
                      className="user-dropdown-item"
                      onClick={() => {
                        closeUserMenu();
                        window.location.href = '/admin';
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Admin
                    </button>
                  )}

                  <button
                    className="user-dropdown-item"
                    onClick={() => {
                      closeUserMenu();
                      if (onNavigateToSettings) {
                        onNavigateToSettings();
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Account Settings
                  </button>

                  <div className="user-dropdown-divider"></div>

                  <div
                    className="user-dropdown-item user-dropdown-toggle-item"
                    onClick={toggleColorScheme}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleColorScheme(); }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 3a9 9 0 010 18V3z" fill="currentColor"/>
                    </svg>
                    <span className="user-dropdown-toggle-label">Changes Color</span>
                    <label className="user-dropdown-toggle-switch" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isRedIncrease}
                        onChange={toggleColorScheme}
                        className="user-dropdown-toggle-input"
                      />
                      <span className="user-dropdown-toggle-slider"></span>
                    </label>
                  </div>
                  <div className="user-dropdown-toggle-hint">
                    {isRedIncrease
                      ? 'Red = increase, Green = decrease'
                      : 'Green = increase, Red = decrease'}
                  </div>

                  <div className="user-dropdown-divider"></div>

                  <button
                    className="user-dropdown-item user-dropdown-item-danger"
                    onClick={() => {
                      closeUserMenu();
                      onSignOut();
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Button
              variant="primary"
              size="medium"
              href="/signin"
              className="unified-header-signin"
            >
              Sign In
            </Button>
          )}

          {/* Hamburger menu button - visible only on tablet/mobile, positioned at far right */}
          {showMarketingPages && (
            <button
              className="unified-header-hamburger"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>
          )}
        </div>

        {/* Mobile menu overlay and drawer */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop overlay */}
            <div
              className="mobile-menu-overlay"
              onClick={closeMobileMenu}
              aria-hidden="true"
            />

            {/* Drawer */}
            <div className="mobile-menu-drawer">
              {/* Close button */}
              <button
                className="mobile-menu-close"
                onClick={closeMobileMenu}
                aria-label="Close menu"
              >
                ×
              </button>

              {/* Menu items */}
              <div className="mobile-menu-items">
                {/* Marketing pages */}
                <button
                  className={`mobile-menu-item ${location.pathname === '/' ? 'active' : ''}`}
                  onClick={handleNavigateHome}
                >
                  Home
                </button>

                <button
                  className="mobile-menu-item"
                  onClick={() => { closeMobileMenu(); window.open('https://docs.membrowse.com', '_blank', 'noopener,noreferrer'); }}
                >
                  Docs
                </button>

                {showMarketingPages && (
                  <button
                    className={`mobile-menu-item ${isOnAboutPage ? 'active' : ''}`}
                    onClick={handleNavigateToAbout}
                  >
                    About
                  </button>
                )}

                {showMarketingPages && (
                  <button
                    className={`mobile-menu-item ${isOnPartnersPage ? 'active' : ''}`}
                    onClick={handleNavigateToPartners}
                  >
                    Partners
                  </button>
                )}

                {showMarketingPages && (
                  <button
                    className={`mobile-menu-item ${isOnPricingPage ? 'active' : ''}`}
                    onClick={handleNavigateToPricing}
                  >
                    Pricing
                  </button>
                )}

                {showMarketingPages && (
                  <button
                    className={`mobile-menu-item ${isOnBlogPage ? 'active' : ''}`}
                    onClick={handleNavigateToBlog}
                  >
                    Blog
                  </button>
                )}

                {showMarketingPages && (
                  <button
                    className={`mobile-menu-item ${isOnShowcasePage ? 'active' : ''}`}
                    onClick={handleNavigateToShowcase}
                  >
                    Showcase
                  </button>
                )}

                {/* Divider between marketing and app navigation */}
                {user && <div className="mobile-menu-divider"></div>}

                {/* App navigation items (from SideMenu) */}
                {user && (
                  <>
                    {/* Projects link */}
                    <button
                      className={`mobile-menu-item mobile-menu-item-with-icon ${location.pathname === '/projects' ? 'active' : ''}`}
                      onClick={() => handleNavigation('/projects')}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Projects
                    </button>

                    {/* Project and Target Info */}
                    {shouldShowDashboard && currentProject && (
                      <div className="mobile-menu-context">
                        <div className="mobile-menu-context-label">Project:</div>
                        <div className="mobile-menu-context-value">{currentProject.name}</div>
                        {decodedTargetName && (
                          <>
                            <div className="mobile-menu-context-label">Target:</div>
                            {availableTargets.length > 1 ? (
                              <TargetSelect
                                targets={availableTargets}
                                value={decodedTargetName}
                                onChange={handleTargetSwitch}
                                className="mobile-menu-target-select"
                              />
                            ) : (
                              <div className="mobile-menu-context-value">{decodedTargetName}</div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Dashboard link */}
                    {shouldShowDashboard && dashboardPath && (
                      <button
                        className={`mobile-menu-item mobile-menu-item-with-icon ${location.pathname === dashboardPath ? 'active' : ''}`}
                        onClick={() => handleNavigation(dashboardPath)}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Dashboard
                      </button>
                    )}

                    {/* Build Comparison link */}
                    {shouldShowDashboard && effectiveProjectId && effectiveTargetName && (
                      <button
                        className={`mobile-menu-item mobile-menu-item-with-icon ${location.pathname.endsWith('/compare') ? 'active' : ''}`}
                        onClick={() => handleNavigation(`/project/${effectiveProjectId}/target/${encodeURIComponent(effectiveTargetName)}/compare`)}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M3 12H9M15 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Compare Builds
                      </button>
                    )}

                    {/* Symbol Analysis link */}
                    {shouldShowDashboard && effectiveProjectId && effectiveTargetName && (
                      <button
                        className={`mobile-menu-item mobile-menu-item-with-icon ${location.pathname.endsWith('/symbols') ? 'active' : ''}`}
                        onClick={() => handleNavigation(`/project/${effectiveProjectId}/target/${encodeURIComponent(effectiveTargetName)}/symbols`)}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                          <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="12" r="1" fill="currentColor"/>
                        </svg>
                        Symbol Analysis
                      </button>
                    )}

                    {/* Budgets link */}
                    {shouldShowDashboard && effectiveProjectId && effectiveTargetName && (
                      <button
                        className={`mobile-menu-item mobile-menu-item-with-icon ${location.pathname.endsWith('/budgets') ? 'active' : ''}`}
                        onClick={() => handleNavigation(`/project/${effectiveProjectId}/target/${encodeURIComponent(effectiveTargetName)}/budgets`)}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="6" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                          <path d="M3 10H21" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="17" cy="14" r="1.5" fill="currentColor"/>
                          <path d="M6 6L8 2L16 2L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Budgets
                      </button>
                    )}

                    {/* Region Configuration link */}
                    {shouldShowDashboard && effectiveProjectId && effectiveTargetName && (
                      <button
                        className={`mobile-menu-item mobile-menu-item-with-icon ${location.pathname.endsWith('/region-config') ? 'active' : ''}`}
                        onClick={() => handleNavigation(`/project/${effectiveProjectId}/target/${encodeURIComponent(effectiveTargetName)}/region-config`)}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" rx="1"/>
                          <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" rx="1"/>
                          <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" rx="1"/>
                          <path d="M14 17.5H21M17.5 14V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Region Config
                      </button>
                    )}

                    {/* Project Settings link */}
                    {shouldShowDashboard && effectiveProjectId && (
                      <button
                        className={`mobile-menu-item mobile-menu-item-with-icon ${location.pathname.includes('/project/') && location.pathname.endsWith('/settings') ? 'active' : ''}`}
                        onClick={() => handleNavigation(`/project/${effectiveProjectId}/settings`)}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.5797 9.96512 20.2585 9.77251 19.9887C9.5799 19.7189 9.31074 19.5141 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.4203 10.0723 3.74151 9.96512 4.0113 9.77251C4.28109 9.5799 4.48594 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.87653 6.85425 4.02405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72669 4.07447C9.90537 3.80618 10.0007 3.49179 10.002 3.17V3C10.002 2.46957 10.2127 1.96086 10.5878 1.58579C10.9629 1.21071 11.4716 1 12.002 1C12.5324 1 13.0411 1.21071 13.4162 1.58579C13.7913 1.96086 14.002 2.46957 14.002 3V3.09C14.0097 3.4203 14.1169 3.74151 14.3095 4.0113C14.5021 4.28109 14.7713 4.48594 15.082 4.6C15.3836 4.73312 15.7182 4.77282 16.0426 4.714C16.367 4.65519 16.6663 4.50054 16.902 4.27L16.962 4.21C17.1477 4.02405 17.3683 3.87653 17.6111 3.77588C17.8539 3.67523 18.1141 3.62343 18.377 3.62343C18.6398 3.62343 18.9001 3.67523 19.1429 3.77588C19.3857 3.87653 19.6062 4.02405 19.792 4.21C19.9779 4.39575 20.1255 4.61632 20.2261 4.85912C20.3268 5.10192 20.3786 5.36217 20.3786 5.625C20.3786 5.88783 20.3268 6.14808 20.2261 6.39088C20.1255 6.63368 19.9779 6.85425 19.792 7.04L19.732 7.1C19.5015 7.33568 19.3468 7.63502 19.288 7.95941C19.2292 8.28381 19.2689 8.61838 19.402 8.92V9C19.5288 9.29577 19.7392 9.54802 20.0075 9.72669C20.2758 9.90537 20.5902 10.0007 20.912 10.002H21C21.5304 10.002 22.0391 10.2127 22.4142 10.5878C22.7893 10.9629 23 11.4716 23 12.002C23 12.5324 22.7893 13.0411 22.4142 13.4162C22.0391 13.7913 21.5304 14.002 21 14.002H20.91C20.5797 14.0097 20.2585 14.1169 19.9887 14.3095C19.7189 14.5021 19.5141 14.7713 19.4 15.082Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Project Settings
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* User info section - shown when authenticated */}
              {user && (
                <div className="mobile-menu-user-section">
                  <div className="mobile-menu-divider"></div>

                  <div className="mobile-menu-user-info">
                    <div className="mobile-menu-user-name">
                      {user.user_metadata?.full_name || 'User'}
                    </div>
                    <div className="mobile-menu-user-email">
                      {user.email}
                    </div>
                  </div>

                  <button
                    className="mobile-menu-item mobile-menu-item-with-icon"
                    onClick={() => {
                      closeMobileMenu();
                      if (onNavigateToSettings) {
                        onNavigateToSettings();
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Account Settings
                  </button>

                  <button
                    className="mobile-menu-item mobile-menu-item-with-icon mobile-menu-item-danger"
                    onClick={() => {
                      closeMobileMenu();
                      onSignOut();
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </>
        )}
        </div>
      </nav>
    </>
  );
};

export default UnifiedHeader;
